import React, { useState, useEffect } from 'react';
import { Cookie, Shield, Settings, X } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';

export const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    // Check if user has already given consent
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Small delay to ensure smooth appearance
      setTimeout(() => setShowBanner(true), 1000);
    }
    // Empty deps: runs once on mount, setShowBanner is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
    // Trigger a custom event to notify CookieWidget
    window.dispatchEvent(new Event('cookieConsentChanged'));
  };

  const handleRejectAll = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
    // Trigger a custom event to notify CookieWidget
    window.dispatchEvent(new Event('cookieConsentChanged'));
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" />

      {/* Cookie Banner */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full bg-white shadow-2xl border-2 border-gray-200 animate-fade-in-up">
          <div className="p-6 sm:p-8">
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center">
                  <Cookie className="w-6 h-6 text-cyan-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    Utilizzo dei Cookie
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Rispettiamo la tua privacy
                  </p>
                </div>
              </div>
            </div>

            {/* Main Content */}
            {!showSettings ? (
              <>
                <div className="mb-6">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Questo sito utilizza cookie tecnici necessari per il funzionamento del sito. 
                    I tuoi dati personali saranno trattati nel rispetto del{' '}
                    <strong>GDPR (Regolamento UE 2016/679)</strong> e delle normative italiane sulla privacy.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Non utilizziamo cookie di profilazione o di terze parti per scopi pubblicitari. 
                    Utilizziamo solo cookie tecnici essenziali per garantire il corretto funzionamento del sito.
                  </p>
                </div>

                {/* Info Box */}
                <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        I tuoi diritti secondo il GDPR
                      </h3>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• Diritto di accesso ai tuoi dati personali</li>
                        <li>• Diritto di rettifica e cancellazione</li>
                        <li>• Diritto di limitazione del trattamento</li>
                        <li>• Diritto alla portabilità dei dati</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Actions - GDPR Equal Prominence */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={handleAcceptAll}
                    className="flex-1 min-h-[56px] bg-white text-gray-900 border-2 border-gray-900 hover:bg-gray-100 font-semibold"
                    size="lg"
                  >
                    Accetta
                  </Button>
                  <Button
                    onClick={handleRejectAll}
                    className="flex-1 min-h-[56px] bg-white text-gray-900 border-2 border-gray-900 hover:bg-gray-100 font-semibold"
                    size="lg"
                  >
                    Rifiuta
                  </Button>
                </div>

                {/* Footer Links */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
                    <a
                      href="/cookie-policy"
                      className="text-cyan-600 hover:text-cyan-700 font-medium"
                    >
                      Leggi la Cookie Policy completa
                    </a>
                    <button
                      onClick={() => setShowSettings(true)}
                      className="text-gray-600 hover:text-gray-900 font-medium"
                    >
                      Gestisci preferenze
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Settings View */}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">
                    Gestisci le tue preferenze sui cookie
                  </h3>
                  
                  {/* Necessary Cookies */}
                  <div className="border border-gray-200 rounded-lg p-4 mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">Cookie Tecnici Necessari</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          Questi cookie sono essenziali per il funzionamento del sito e non possono essere disabilitati.
                        </p>
                      </div>
                      <div className="ml-4">
                        <div className="w-12 h-6 bg-cyan-500 rounded-full flex items-center px-1">
                          <div className="w-4 h-4 bg-white rounded-full ml-auto"></div>
                        </div>
                        <span className="text-xs text-gray-500 mt-1 block">Sempre attivi</span>
                      </div>
                    </div>
                  </div>

                  {/* Analytics Cookies */}
                  <div className="border border-gray-200 rounded-lg p-4 mb-3 opacity-50">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">Cookie Analitici</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          Non utilizzati su questo sito.
                        </p>
                      </div>
                      <div className="ml-4">
                        <div className="w-12 h-6 bg-gray-300 rounded-full flex items-center px-1">
                          <div className="w-4 h-4 bg-white rounded-full"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Marketing Cookies */}
                  <div className="border border-gray-200 rounded-lg p-4 opacity-50">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">Cookie di Marketing</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          Non utilizzati su questo sito.
                        </p>
                      </div>
                      <div className="ml-4">
                        <div className="w-12 h-6 bg-gray-300 rounded-full flex items-center px-1">
                          <div className="w-4 h-4 bg-white rounded-full"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Settings Actions */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={handleSavePreferences}
                    className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white"
                    size="lg"
                  >
                    Salva Preferenze
                  </Button>
                  <Button
                    onClick={() => setShowSettings(false)}
                    variant="outline"
                    className="flex-1 border-2 border-gray-300 hover:bg-gray-50"
                    size="lg"
                  >
                    Indietro
                  </Button>
                </div>
              </>
            )}
          </div>
        </Card>
      </div>
    </>
  );
};